import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { lawnServiceProviderInfoService } from '../../../../services/lawnServiceProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('LawnServiceProviderInput');

export const LawnServiceProviderInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { lawnServiceProviderId?: string; isEdit?: boolean }>, string>>();
  const { lawnServiceProviderId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedLawnServiceProviderId, setSavedLawnServiceProviderId] = useState<string | undefined>();
  
  const [formData, setFormData] = useState({
    companyName: '',
    serviceType: '',
    contactPhone: '',
    emergencyPhone: '',
    website: '',
    serviceAddress: '',
    billingAddress: '',
    serviceFrequency: '',
    estimatedBalance: '',
    contractStartDate: '',
    contractEndDate: '',
    notes: '',
    accountNumber: '',
    username: '',
    password: '',
  });

  const serviceTypes = ['Lawn Mowing', 'Landscaping', 'Tree Service', 'Pest Control', 'Irrigation', 'Full Service', 'Other'];

  // Load existing lawn service provider data when editing
  useEffect(() => {
    logger.debug('[Edit Debug] useEffect triggered - isEdit:', isEdit, 'lawnServiceProviderId:', lawnServiceProviderId);
    if (isEdit && lawnServiceProviderId) {
      logger.debug('[Edit Debug] Loading lawn service provider data for editing');
      loadLawnServiceProviderData();
    }
  }, [isEdit, lawnServiceProviderId]);

  const loadLawnServiceProviderData = async () => {
    try {
      logger.debug('[Edit Debug] loadLawnServiceProviderData called with ID:', lawnServiceProviderId);
      if (!lawnServiceProviderId) {
        logger.debug('[Edit Debug] No lawnServiceProviderId provided, returning');
        return;
      }
      
      logger.debug('[Edit Debug] Fetching data for ID:', lawnServiceProviderId);
      const data = await lawnServiceProviderInfoService.getLawnServiceProviderInfoById(lawnServiceProviderId);
      logger.debug('[Edit Debug] Received data:', data);
      
      if (data) {
        setFormData({
          companyName: data.companyName || '',
          serviceType: data.serviceType || '',
          contactPhone: formatPhoneNumber(data.contactPhone || ''),
          emergencyPhone: formatPhoneNumber(data.emergencyPhone || ''),
          website: data.website || '',
          serviceAddress: data.serviceAddress || '',
          billingAddress: data.billingAddress || '',
          serviceFrequency: data.serviceFrequency || '',
          estimatedBalance: data.estimatedBalance ? formatCurrency(data.estimatedBalance.toString()) : '',
          contractStartDate: data.contractStartDate || '',
          contractEndDate: data.contractEndDate || '',
          notes: data.notes || '',
          accountNumber: '',
          username: '',
          password: '',
        });

        // Load portal credentials
        try {
          const portalData = await vaultService.getPortalCredentials(lawnServiceProviderId, 'lawn-service-provider');
          if (portalData) {
            setFormData(prev => ({
              ...prev,
              accountNumber: portalData.accountNumber || '',
              username: portalData.username || '',
              password: portalData.password || '',
            }));
            setHasOnlinePortal(true);
          }
        } catch (vaultError) {
          logger.debug('Could not load portal credentials:', vaultError);
        }
      }
    } catch (error) {
      logger.error('Error loading lawn service provider data:', error);
      Alert.alert('Error', 'Failed to load lawn service provider information');
    }
  };

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  const handleCurrencyChange = (text: string) => {
    const cleaned = text.replace(/^\$/, '');
    setFormData({ ...formData, estimatedBalance: formatCurrency(cleaned) });
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const handlePhoneChange = (field: string, text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleServiceTypeSelect = (type: string) => {
    setFormData({ ...formData, serviceType: type });
    setShowServiceTypeModal(false);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.accountNumber || formData.username || formData.password) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      
      // Basic validation
      if (!formData.companyName) {
        Alert.alert('Validation Error', 'Company name is required');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        companyName: formData.companyName,
        serviceType: formData.serviceType || undefined,
        contactPhone: formData.contactPhone.replace(/\D/g, '') || undefined,
        emergencyPhone: formData.emergencyPhone.replace(/\D/g, '') || undefined,
        website: formData.website || undefined,
        serviceAddress: formData.serviceAddress || undefined,
        billingAddress: formData.billingAddress || undefined,
        serviceFrequency: formData.serviceFrequency || undefined,
        estimatedBalance: formData.estimatedBalance ? parseFloat(formData.estimatedBalance.replace(/[^0-9.]/g, '')) : undefined,
        contractStartDate: formData.contractStartDate || undefined,
        contractEndDate: formData.contractEndDate || undefined,
        notes: formData.notes || undefined,
      };

      // Create or update lawn service provider record
      let saved;
      let savedId;

      if (isEdit && lawnServiceProviderId) {
        saved = await lawnServiceProviderInfoService.updateLawnServiceProviderInfo(lawnServiceProviderId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = saved?.data || saved;
        savedId = updatedData.id || lawnServiceProviderId;
      } else {
        saved = await lawnServiceProviderInfoService.createLawnServiceProviderInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }
      
      // Store the saved ID for the Done button
      setSavedLawnServiceProviderId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.accountNumber || formData.username || formData.password)) {
        try {
          await vaultService.storePortalCredentials(savedId, 'lawn-service-provider', {
            accountNumber: formData.accountNumber,
            username: formData.username,
            password: formData.password,
          });
        } catch (e) {
          logger.error('Vault write failed for lawn service provider portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Lawn service provider information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('LawnServiceProviderLockUnlock', { lawnServiceProviderId: lawnServiceProviderId })
          }
        ]);
      } else {
        // Clear the form and reset states for Add Another
        setFormData({
          companyName: '',
          serviceType: '',
          contactPhone: '',
          emergencyPhone: '',
          website: '',
          serviceAddress: '',
          billingAddress: '',
          serviceFrequency: '',
          estimatedBalance: '',
          contractStartDate: '',
          contractEndDate: '',
          notes: '',
          accountNumber: '',
          username: '',
          password: '',
        });
        setHasOnlinePortal(false);
        
        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset submitting so buttons are interactive
        setSubmitting(false);
      }
    } catch (error: any) {
      logger.error('Error saving lawn service provider info:', error);
      Alert.alert('Error', 'Failed to save lawn service provider information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      companyName: '',
      serviceType: '',
      contactPhone: '',
      emergencyPhone: '',
      website: '',
      serviceAddress: '',
      billingAddress: '',
      serviceFrequency: '',
      estimatedBalance: '',
      contractStartDate: '',
      contractEndDate: '',
      notes: '',
      accountNumber: '',
      username: '',
      password: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedLawnServiceProviderId);
    (navigation as any).navigate('LawnServiceProviderLockUnlock', { lawnServiceProviderId: savedLawnServiceProviderId });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Lawn Service Provider Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.companyName}
              onChangeText={(text) => setFormData({ ...formData, companyName: text })}
              placeholder="Ex: Green Acres Lawn Care"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Type</Text>
            <TouchableOpacity
              style={[styles.input, styles.dropdownButton]}
              onPress={() => setShowServiceTypeModal(true)}
            >
              <Text style={[styles.inputText, !formData.serviceType && styles.placeholderText]}>
                {formData.serviceType || 'Select service type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contact Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.contactPhone}
              onChangeText={(text) => handlePhoneChange('contactPhone', text)}
              placeholder="Ex: (555) 123-4567"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.emergencyPhone}
              onChangeText={(text) => handlePhoneChange('emergencyPhone', text)}
              placeholder="Ex: (555) 911-1234"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.input}
              value={formData.website}
              onChangeText={(text) => setFormData({ ...formData, website: text })}
              placeholder="Ex: www.greenacres.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle - Moved after website */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your lawn service provider have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Address</Text>
            <TextInput
              style={styles.input}
              value={formData.serviceAddress}
              onChangeText={(text) => setFormData({ ...formData, serviceAddress: text })}
              placeholder="Address where service is provided"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Billing Address</Text>
            <TextInput
              style={styles.input}
              value={formData.billingAddress}
              onChangeText={(text) => setFormData({ ...formData, billingAddress: text })}
              placeholder="Address for billing"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Frequency</Text>
            <TextInput
              style={styles.input}
              value={formData.serviceFrequency}
              onChangeText={(text) => setFormData({ ...formData, serviceFrequency: text })}
              placeholder="Ex: Weekly, Bi-weekly, Monthly"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monthly amount</Text>
            <TextInput
              style={styles.input}
              value={formData.estimatedBalance ? `$${formData.estimatedBalance}` : ''}
              onChangeText={handleCurrencyChange}
              placeholder="$0.00"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contract Start Date</Text>
            <TextInput
              style={styles.input}
              value={formData.contractStartDate}
              onChangeText={(text) => setFormData({ ...formData, contractStartDate: text })}
              placeholder="Ex: 01/01/2024"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contract End Date</Text>
            <TextInput
              style={styles.input}
              value={formData.contractEndDate}
              onChangeText={(text) => setFormData({ ...formData, contractEndDate: text })}
              placeholder="Ex: 31/12/2024"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional notes or special instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>



          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity 
                style={styles.addAnotherButton} 
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.doneButton} 
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>
                    {isEdit ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      {/* Service Type Modal */}
      <Modal
        visible={showServiceTypeModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowServiceTypeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Service Type</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowServiceTypeModal(false)}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {serviceTypes.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={styles.stateItem}
                  onPress={() => handleServiceTypeSelect(type)}
                >
                  <Text style={styles.stateItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  uploadContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: ms(16),
    paddingHorizontal: ms(4),
  },
  uploadButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    flexDirection: 'row',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    flex: 0.45,
    justifyContent: 'center',
  },
  uploadIcon: {
    width: s(20),
    height: s(20),
    marginRight: ms(8),
  },
  uploadText: {
    color: '#1C2C4F',
    fontSize: ms(16, 0.3),
    fontWeight: '500',
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  content: {
    flex: 1,
    padding: ms(20),
    marginBottom: ms(100),
  },
  button: {
    width: '100%',
    borderRadius: ms(30),
    padding: ms(15),
    alignItems: 'center',
    backgroundColor: '#29BFC0',
    marginTop: 'auto',
    marginBottom: ms(20),
  },
  buttonText: {
    fontSize: ms(18, 0.3),
    fontWeight: '500',
    color: '#FFFFFF',
  },
});
